/**
 * Image compression utility for mobile uploads
 */

export interface CompressionOptions {
  maxSizeMB: number;
  quality: number;
  maxWidthOrHeight: number;
  format: 'jpeg' | 'webp' | 'png';
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

/**
 * Compresses an image file using HTML5 Canvas
 */
export const compressImage = async (
  file: File, 
  options: Partial<CompressionOptions> = {}
): Promise<CompressionResult> => {
  const defaultOptions: CompressionOptions = {
    maxSizeMB: 2,
    quality: 0.8,
    maxWidthOrHeight: 1920,
    format: 'jpeg'
  };

  const config = { ...defaultOptions, ...options };
  const originalSize = file.size;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > config.maxWidthOrHeight || height > config.maxWidthOrHeight) {
        const ratio = Math.min(
          config.maxWidthOrHeight / width,
          config.maxWidthOrHeight / height
        );
        width *= ratio;
        height *= ratio;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      // Try compression with different quality levels if needed
      compressWithQuality(canvas, config, originalSize, resolve, reject);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Attempts compression with progressive quality reduction
 */
const compressWithQuality = (
  canvas: HTMLCanvasElement,
  config: CompressionOptions,
  originalSize: number,
  resolve: (result: CompressionResult) => void,
  reject: (error: Error) => void
) => {
  const qualities = [config.quality, 0.6, 0.4, 0.2];
  let currentQualityIndex = 0;

  const tryCompress = () => {
    const quality = qualities[currentQualityIndex];
    const mimeType = `image/${config.format}`;
    
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to compress image'));
          return;
        }

        const compressedSize = blob.size;
        const targetSize = config.maxSizeMB * 1024 * 1024;

        // If size is acceptable or we've tried all qualities, return result
        if (compressedSize <= targetSize || currentQualityIndex >= qualities.length - 1) {
          const timestamp = Date.now();
          const originalName = 'compressed_image';
          const extension = config.format === 'jpeg' ? 'jpg' : config.format;
          const fileName = `${originalName}_${timestamp}.${extension}`;

          const compressedFile = new File([blob], fileName, { 
            type: mimeType,
            lastModified: Date.now()
          });

          resolve({
            file: compressedFile,
            originalSize,
            compressedSize,
            compressionRatio: Math.round((1 - compressedSize / originalSize) * 100)
          });
        } else {
          // Try next quality level
          currentQualityIndex++;
          tryCompress();
        }
      },
      mimeType,
      quality
    );
  };

  tryCompress();
};

/**
 * Creates a thumbnail version of the image
 */
export const createThumbnail = async (file: File): Promise<File> => {
  return compressImage(file, {
    maxSizeMB: 0.1,
    quality: 0.7,
    maxWidthOrHeight: 200,
    format: 'jpeg'
  }).then(result => result.file);
};

/**
 * Checks if compression is recommended based on file size
 */
export const shouldCompress = (file: File): boolean => {
  const sizeInMB = file.size / (1024 * 1024);
  return sizeInMB > 1; // Recommend compression for files > 1MB
};

/**
 * Gets optimal compression settings based on file size and Android compatibility
 */
export const getOptimalCompressionSettings = (file: File): Partial<CompressionOptions> => {
  const sizeInMB = file.size / (1024 * 1024);
  
  if (sizeInMB > 8) {
    // Very large files - aggressive compression
    return {
      maxSizeMB: 1.5,
      quality: 0.6,
      maxWidthOrHeight: 1200,
      format: 'jpeg'
    };
  } else if (sizeInMB > 4) {
    // Large files - moderate compression
    return {
      maxSizeMB: 2,
      quality: 0.7,
      maxWidthOrHeight: 1600,
      format: 'jpeg'
    };
  } else if (sizeInMB > 1) {
    // Medium files - light compression
    return {
      maxSizeMB: 2.5,
      quality: 0.8,
      maxWidthOrHeight: 1920,
      format: 'jpeg'
    };
  }
  
  // Small files - minimal or no compression
  return {
    maxSizeMB: 3,
    quality: 0.9,
    maxWidthOrHeight: 1920,
    format: 'jpeg'
  };
};